<template>
  <v-container fluid class="pa-6">
    <!-- KPI Cards Section -->
    <v-row class="mb-4 mb-md-6">
      <v-col cols="6" sm="6" lg="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-3 pa-sm-5">
            <div class="kpi-icon d-none d-sm-flex">
              <v-icon color="primary">mdi-package-variant</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Productos</div>
              <div class="kpi-value">{{ items.length }}</div>
              <div class="kpi-subtitle d-none d-sm-flex">
                <v-icon x-small color="info">mdi-archive</v-icon>
                En traslado
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="6" sm="6" lg="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-3 pa-sm-5">
            <div class="kpi-icon d-none d-sm-flex">
              <v-icon color="success">mdi-cube-outline</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Total Unidades</div>
              <div class="kpi-value">{{ totals.qty }}</div>
              <div class="kpi-subtitle d-none d-sm-flex">Cantidad total</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="6" sm="6" lg="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-3 pa-sm-5">
            <div class="kpi-icon d-none d-sm-flex">
              <v-icon color="warning">mdi-cash</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Valor Total</div>
              <div class="kpi-value">{{ formatCurrency(totals.total) }}</div>
              <div class="kpi-subtitle d-none d-sm-flex">Valor en traslado</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="6" sm="6" lg="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-3 pa-sm-5">
            <div class="kpi-icon d-none d-sm-flex">
              <v-icon color="info">mdi-store</v-icon>
            </div>
            <div class="kpi-content">
              <div class="kpi-label">Bodega Destino</div>
              <div class="kpi-value text-truncate">{{ client.PartyName || "-" }}</div>
              <div class="kpi-subtitle d-none d-sm-flex">
                <v-icon x-small color="success">mdi-truck-fast</v-icon>
                {{ client.PartyIdentification || "Sin seleccionar" }}
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Origin and Destination Cards -->
    <v-row class="mb-4">
      <v-col cols="12" md="6">
        <v-card class="info-card elevation-0" outlined>
          <v-card-title class="pa-4 pb-3">
            <v-icon color="primary" class="mr-2">mdi-home-export-outline</v-icon>
            <span class="card-title">Bodega Origen</span>
          </v-card-title>
          <v-card-text class="pa-4 pt-0">
            <div class="info-item">
              <strong>{{ store.PartyName }}</strong>
            </div>
            <div class="info-item text-caption grey--text">
              {{ store.store_address + ", " + store.store_city }}
            </div>
            <div class="info-item text-caption grey--text mt-2">
              ID: {{ store.store_id }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card class="info-card elevation-0" outlined>
          <v-card-title class="pa-4 pb-3">
            <v-icon color="success" class="mr-2">mdi-home-import-outline</v-icon>
            <span class="card-title">Bodega Destino</span>
          </v-card-title>
          <v-card-text class="pa-4 pt-0">
            <v-select
              :items="stores"
              label="Seleccionar bodega destino"
              dense
              item-value="store_id"
              item-text="store_name"
              outlined
              clearable
              @change="changeStore"
              hide-details="auto"
              return-object
              prepend-inner-icon="mdi-store"
              class="mb-3"
            />
            <div v-if="client.PartyName" class="destination-info">
              <div class="info-item text-caption">
                <v-icon x-small class="mr-1">mdi-identifier</v-icon>
                ID: {{ client.PartyIdentification }}
              </div>
              <div class="info-item text-caption">
                <v-icon x-small class="mr-1">mdi-email</v-icon>
                {{ client.client_email }}
              </div>
              <div class="info-item text-caption">
                <v-icon x-small class="mr-1">mdi-phone</v-icon>
                {{ client.client_phone }}
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Products Table Card -->
    <v-card class="main-card elevation-0" outlined>
      <v-card-title>
        <h2 class="section-title" :class="{ 'text-h6': $vuetify.breakpoint.mobile }">
          Productos a Trasladar
        </h2>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          @click="search_dialog = true"
          :small="$vuetify.breakpoint.mobile"
        >
          <v-icon left>mdi-plus</v-icon>
          <span class="d-none d-sm-inline">Agregar Producto</span>
          <span class="d-inline d-sm-none">Agregar</span>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <!-- Desktop Table / Mobile List -->
        <template v-if="$vuetify.breakpoint.mdAndUp">
          <!-- Desktop Table View -->
          <v-data-table
            :headers="headers_items"
            :items="items"
            :items-per-page="10"
            item-key="ean"
            :loading="loading_status"
            loader-height="3"
            loading-text="Cargando productos..."
            class="minimal-table"
            :footer-props="{
              'items-per-page-options': [10, 25, 50, 100],
            }"
          >
            <!-- Product Info -->
            <template v-slot:[`item.product`]="{ item }">
              <div class="product-info">
                <div class="product-main">
                  {{ item.marca }} - {{ item.refe }} - {{ item.color }}
                </div>
                <div class="product-details text-caption grey--text">
                  {{ get_categoria_name(item.categoria) }} | Talla: {{ item.talla }}
                </div>
              </div>
            </template>

            <!-- Price -->
            <template v-slot:[`item.precio`]="{ item }">
              <div class="text-right">
                {{ formatCurrency(item.precio) }}
              </div>
            </template>

            <!-- Quantity -->
            <template v-slot:[`item.cantidad`]="{ item }">
              <v-edit-dialog
                :return-value.sync="item.cantidad"
                @save="updateTotal"
                save-text="Guardar"
                cancel-text="Cancelar"
                large
              >
                <div class="quantity-edit">
                  <v-chip small label color="primary" outlined>
                    {{ item.cantidad }} und
                  </v-chip>
                </div>
                <template v-slot:input>
                  <v-text-field
                    label="Cantidad"
                    type="number"
                    :rules="[
                      f_qty.qtyMin(item.cantidad, 1),
                      f_qty.qtyMax(item.cantidad, item.qty),
                    ]"
                    v-model="item.cantidad"
                    @input="checkQty(item)"
                    single-line
                    outlined
                    :hint="qtyAlert"
                    class="pt-4"
                    dense
                  />
                </template>
              </v-edit-dialog>
            </template>

            <!-- Subtotal -->
            <template v-slot:[`item.subtotal`]="{ item }">
              <div class="text-right font-weight-medium">
                {{ formatCurrency(item.precio * item.cantidad) }}
              </div>
            </template>

            <!-- Actions -->
            <template v-slot:[`item.actions`]="{ item }">
              <v-btn icon small @click="deleteItem(item)">
                <v-icon small color="error">mdi-delete-outline</v-icon>
              </v-btn>
            </template>

            <!-- Footer with totals -->
            <template v-slot:body.append v-if="items.length > 0">
              <tr class="grey lighten-4">
                <td colspan="3" class="text-right font-weight-bold">TOTALES</td>
                <td class="text-center font-weight-bold">{{ totals.qty }} und</td>
                <td class="text-right font-weight-bold">
                  {{ formatCurrency(totals.total) }}
                </td>
                <td></td>
              </tr>
            </template>

            <!-- Empty State -->
            <template v-slot:no-data>
              <div class="text-center py-8">
                <v-icon size="48" color="grey lighten-2">mdi-package-variant</v-icon>
                <div class="text-h6 mt-2 grey--text">No hay productos agregados</div>
                <div class="text-caption grey--text">
                  Haz clic en "Agregar Producto" para comenzar
                </div>
              </div>
            </template>
          </v-data-table>
        </template>

        <template v-else>
          <!-- Mobile List View -->
          <v-list v-if="items.length > 0" class="py-0">
            <template v-for="(item, index) in items">
              <v-list-item :key="item.ean" class="px-4 py-3">
                <v-list-item-content>
                  <div class="d-flex justify-space-between align-start mb-2">
                    <div class="flex-grow-1 mr-2">
                      <div class="font-weight-medium">
                        {{ item.marca }} - {{ item.refe }}
                      </div>
                      <div class="text-caption grey--text">
                        {{ item.color }} | Talla: {{ item.talla }}
                      </div>
                    </div>
                    <v-btn icon small @click="deleteItem(item)">
                      <v-icon small color="error">mdi-delete-outline</v-icon>
                    </v-btn>
                  </div>

                  <v-row no-gutters class="mt-3">
                    <v-col cols="4">
                      <div class="text-caption grey--text">Precio</div>
                      <div class="font-weight-medium">
                        {{ formatCurrency(item.precio) }}
                      </div>
                    </v-col>
                    <v-col cols="4" class="text-center">
                      <div class="text-caption grey--text">Cantidad</div>
                      <v-chip
                        small
                        label
                        color="primary"
                        outlined
                        @click="openQuantityDialog(item)"
                      >
                        {{ item.cantidad }} und
                      </v-chip>
                    </v-col>
                    <v-col cols="4" class="text-right">
                      <div class="text-caption grey--text">Subtotal</div>
                      <div class="font-weight-medium">
                        {{ formatCurrency(item.precio * item.cantidad) }}
                      </div>
                    </v-col>
                  </v-row>
                </v-list-item-content>
              </v-list-item>
              <v-divider v-if="index < items.length - 1" :key="`div-${item.ean}`" />
            </template>

            <!-- Mobile Totals -->
            <v-list-item class="grey lighten-4 py-3">
              <v-list-item-content>
                <div class="font-weight-bold text-center mb-2">TOTALES</div>
                <v-row no-gutters>
                  <v-col cols="6">
                    <div class="text-caption">Cantidad Total</div>
                    <div class="font-weight-bold">{{ totals.qty }} und</div>
                  </v-col>
                  <v-col cols="6" class="text-right">
                    <div class="text-caption">Valor Total</div>
                    <div class="font-weight-bold">{{ formatCurrency(totals.total) }}</div>
                  </v-col>
                </v-row>
              </v-list-item-content>
            </v-list-item>
          </v-list>

          <!-- Empty State Mobile -->
          <div v-else class="text-center py-12">
            <v-icon size="64" color="grey lighten-2">mdi-package-variant</v-icon>
            <h3 class="text-h6 mt-4 grey--text">No hay productos agregados</h3>
            <p class="grey--text mt-2">Toca "Agregar" para comenzar</p>
          </div>
        </template>
      </v-card-text>
    </v-card>

    <!-- Motive Card -->
    <v-card class="main-card elevation-0" outlined>
      <v-form v-model="validatemotive" ref="motiveform" @submit.prevent="save_receipt">
        <v-card-title class="pa-4 pb-3">
          <v-icon color="info" class="mr-2">mdi-text-box-outline</v-icon>
          <span class="card-title">Motivo del Traslado</span>
        </v-card-title>
        <v-card-text class="pa-4 pt-0">
          <v-select
            v-model="motive"
            hide-details="auto"
            :items="FP"
            item-value="codigo"
            item-text="valor"
            label="Seleccionar motivo"
            return-object
            outlined
            :rules="f_required"
            prepend-inner-icon="mdi-clipboard-text"
          />
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn
            large
            color="success"
            :disabled="!valide_qry"
            type="submit"
            :loading="loading_status"
          >
            <v-icon left>mdi-check</v-icon>
            Registrar Traslado
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>

    <!-- Search Dialog -->
    <v-dialog v-model="search_dialog" max-width="900px" scrollable>
      <v-card>
        <v-card-title class="pa-0">
          <v-toolbar flat class="dialog-toolbar">
            <v-icon class="mr-2">mdi-magnify</v-icon>
            <h3 class="dialog-title">Buscar en Inventario</h3>
            <v-spacer></v-spacer>
            <v-btn icon @click="search_dialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
        </v-card-title>

        <v-card-text class="pa-0">
          <v-form
            v-model="search_valid"
            ref="searchform"
            @submit.prevent="search_inventory"
          >
            <!-- Search Filters -->
            <v-container fluid class="search-filters pa-4">
              <v-row>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="filters.field"
                    :items="['CATEGORIA', 'GRUPO', 'MARCA', 'REFE', 'COLOR', 'TALLA']"
                    label="Criterio de búsqueda"
                    outlined
                    dense
                    hide-details="auto"
                  />
                </v-col>
                <v-col cols="12" md="5">
                  <v-text-field
                    v-model="filters.value"
                    label="Buscar"
                    outlined
                    dense
                    clearable
                    hide-details="auto"
                    prepend-inner-icon="mdi-magnify"
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-btn
                    :disabled="!search_valid"
                    block
                    color="primary"
                    type="submit"
                    :loading="loading_status"
                  >
                    <v-icon left>mdi-magnify</v-icon>
                    Buscar
                  </v-btn>
                </v-col>
              </v-row>
            </v-container>

            <v-divider></v-divider>

            <!-- Results Table -->
            <v-data-table
              :headers="search_headers"
              :items="inventory_lst"
              :items-per-page="10"
              :loading="loading_status"
              loading-text="Buscando productos..."
              class="search-table"
              height="400"
              fixed-header
            >
              <template v-slot:[`item.product`]="{ item }">
                <div class="product-info">
                  <div class="product-main">
                    {{ item.marca }} - {{ item.refe }} - {{ item.color }}
                  </div>
                  <div class="product-details text-caption grey--text">
                    {{ get_categoria_name(item.categoria) }} | Talla: {{ item.talla }}
                  </div>
                </div>
              </template>

              <template v-slot:[`item.precio`]="{ item }">
                <div class="text-right">{{ formatCurrency(item.precio) }}</div>
              </template>

              <template v-slot:[`item.cantidad`]="{ item }">
                <v-chip small label :color="item.cantidad > 0 ? 'success' : 'error'" dark>
                  {{ item.cantidad }} und
                </v-chip>
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <v-btn
                  icon
                  small
                  color="primary"
                  @click="add2cart(item)"
                  :disabled="item.cantidad <= 0"
                >
                  <v-icon small>mdi-plus-circle</v-icon>
                </v-btn>
              </template>

              <!-- Empty state -->
              <template v-slot:no-data>
                <div class="text-center py-8">
                  <v-icon size="48" color="grey lighten-2">mdi-magnify</v-icon>
                  <div class="text-h6 mt-2 grey--text">
                    {{ loading_status ? "Buscando..." : "No se encontraron productos" }}
                  </div>
                </div>
              </template>
            </v-data-table>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Print Navigation Drawer -->
    <v-navigation-drawer v-model="showPrint" right app temporary width="400">
      <v-card height="100%">
        <v-toolbar dark color="primary">
          <v-toolbar-title>Traslado de Bodega</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="showPrint = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-4">
          <v-img :src="dataURL" contain></v-img>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-btn block large color="success" @click="print_doc">
            <v-icon left>mdi-printer</v-icon>
            Imprimir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-navigation-drawer>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      top
      right
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false"> Cerrar </v-btn>
      </template>
    </v-snackbar>

    <!-- Mobile Quantity Dialog -->
    <v-dialog v-model="quantityDialog" max-width="300">
      <v-card v-if="editingItem">
        <v-card-title>Editar Cantidad</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="editingItem.cantidad"
            label="Cantidad"
            type="number"
            outlined
            autofocus
            :rules="[
              f_qty.qtyMin(editingItem.cantidad, 1),
              f_qty.qtyMax(editingItem.cantidad, editingItem.qty),
            ]"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="quantityDialog = false">Cancelar</v-btn>
          <v-btn color="primary" @click="saveQuantity">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { webserver, formatCurrency } from "@/services/webserver.js";
import createClient from "@/utils/createClient.js";

export default {
  name: "Warehouse",
  data() {
    return {
      // UI State
      loading_status: false,
      search_dialog: false,
      showPrint: false,
      search_valid: false,
      validatemotive: false,
      valide_qry: false,
      quantityDialog: false,
      editingItem: null,

      // Snackbar
      snackbar: {
        show: false,
        text: "",
        color: "info",
        timeout: 3000,
      },

      // Data
      items: [],
      inventory_lst: [],
      stores: [],
      FP: [],

      // Models
      store: createClient(),
      client: {
        PartyName: null,
        PartyIdentification: null,
        client_email: null,
        client_phone: null,
      },
      motive: { codigo: null, valor: null },
      totals: { qty: 0, total: 0, tax: 0, paid: 0, unpaid: 0 },

      // Filters
      filters: { field: "MARCA", value: "" },

      // Table Headers
      headers_items: [
        {
          text: "Producto",
          value: "product",
          sortable: false,
          width: "40%",
        },
        {
          text: "Precio",
          value: "precio",
          align: "right",
          width: "15%",
        },
        {
          text: "Cantidad",
          value: "cantidad",
          align: "center",
          width: "15%",
        },
        {
          text: "Subtotal",
          value: "subtotal",
          align: "right",
          width: "20%",
        },
        {
          text: "",
          value: "actions",
          align: "center",
          sortable: false,
          width: "10%",
        },
      ],

      search_headers: [
        {
          text: "Producto",
          value: "product",
          sortable: false,
          width: "50%",
        },
        {
          text: "Precio",
          value: "precio",
          align: "right",
          width: "20%",
        },
        {
          text: "Disponible",
          value: "cantidad",
          align: "center",
          width: "20%",
        },
        {
          text: "",
          value: "actions",
          align: "center",
          sortable: false,
          width: "10%",
        },
      ],

      // Validation
      f_required: [(v) => !!v || "Campo requerido"],
      f_qty: {
        qtyMin(value, min) {
          return (parseFloat(value) || "") >= min || `Cantidad mínima ${min}`;
        },
        qtyMax(value, max) {
          return (parseFloat(value) || "") <= max || `Cantidad máxima ${max}`;
        },
      },

      // Others
      dataURL: "",
      qtyAlert: "",
    };
  },

  mounted() {
    console.log(window.settings);
    this.store = window.store;
    this.FP = window.settings["TB"] || [];
    this.get_stores();
  },

  methods: {
    // Format methods
    formatCurrency,

    // Message handling
    showMessage(text, color = "info", timeout = 3000) {
      this.snackbar = {
        show: true,
        text,
        color,
        timeout,
      };
    },

    get_categoria_name(codigo) {
      if (!codigo) return "";
      // Buscar en window.settings["CATEGORIA"] que es un array
      var categorias = window.settings && window.settings["CATEGORIA"] 
        ? window.settings["CATEGORIA"].find(item => item.codigo === codigo)
        : null;
      // Devolver el valor si existe y es diferente del código
      return categorias && categorias.valor && categorias.valor !== codigo 
        ? categorias.valor : codigo;
    },

    get_grupo_name(codigo) {
      if (!codigo) return "";
      // Buscar en window.settings["GRUPO"] que es un array
      var grupos = window.settings && window.settings["GRUPO"] 
        ? window.settings["GRUPO"].find(item => item.codigo === codigo)
        : null;
      // Devolver el valor si existe y es diferente del código
      return grupos && grupos.valor && grupos.valor !== codigo 
        ? grupos.valor : codigo;
    },

    // Store methods
    changeStore(e) {
      if (e) {
        this.client = {
          PartyName: e.store_name,
          PartyIdentification: e.store_id,
          client_email: e.store_email,
          client_phone: e.store_phone,
        };
      } else {
        this.client = {
          PartyName: null,
          PartyIdentification: null,
          client_email: null,
          client_phone: null,
        };
      }
    },

    get_stores() {
      const qry = {
        store: window.store.store_id,
        table: "stores",
        filters: [
          {
            field: "store_owner",
            operator: "=",
            value: window.store.store_owner,
          },
        ],
      };
      console.log(qry);

      this.loading_status = true;
      webserver(
        "get_table",
        qry,
        (data) => {
          const index = data.findIndex((itm) => itm.store_id === window.store.store_id);
          if (index >= 0) data.splice(index, 1);
          this.loading_status = false;
          this.stores = data;
        },
        (error) => {
          this.loading_status = false;
          this.showMessage("Error al cargar bodegas: " + error, "error");
        }
      );
    },

    // Product methods
    search_inventory() {
      if (!this.filters.value) {
        this.showMessage("Por favor ingrese un criterio de búsqueda", "warning");
        return;
      }

      const qry = {
        store: window.store.store_id,
        dbSearch: "inventory",
        filters: [
          {
            field: "id_pos",
            operator: "=",
            value: window.store.store_id,
          },
          {
            field: this.filters.field,
            operator: " LIKE ",
            value: this.filters.value,
          },
        ],
      };

      this.loading_status = true;
      webserver(
        "get_catalog",
        qry,
        (data) => {
          this.loading_status = false;
          this.inventory_lst = data;
          if (data.length === 0) {
            this.showMessage("No se encontraron productos", "info");
          }
        },
        (error) => {
          this.loading_status = false;
          this.showMessage("Error en la búsqueda: " + error, "error");
        }
      );
    },

    add2cart(item) {
      const newItem = { ...item };
      newItem.qty = newItem.cantidad;
      newItem.cantidad = 1;

      const index = this.items.findIndex((itm) => itm.ean === newItem.ean);
      if (index === -1) {
        this.items.push(newItem);
        this.showMessage("Producto agregado", "success");
      } else {
        if (this.items[index].cantidad < this.items[index].qty) {
          this.items[index].cantidad += 1;
          this.showMessage("Cantidad actualizada", "success");
        } else {
          this.showMessage("Cantidad máxima alcanzada", "warning");
        }
      }
      this.updateTotal();
    },

    deleteItem(item) {
      const index = this.items.findIndex((itm) => itm.ean === item.ean);
      if (index > -1) {
        this.items.splice(index, 1);
        this.updateTotal();
        this.showMessage("Producto eliminado", "info");
      }
    },

    checkQty(item) {
      if (parseFloat(item.cantidad) > parseFloat(item.qty)) {
        item.cantidad = parseFloat(item.qty);
        this.qtyAlert = `Cantidad máxima: ${parseFloat(item.qty)}`;
      }
      if (parseFloat(item.cantidad) < 1) {
        item.cantidad = 1;
        this.qtyAlert = "Cantidad mínima: 1";
      }
    },

    // Mobile quantity edit
    openQuantityDialog(item) {
      this.editingItem = { ...item };
      this.quantityDialog = true;
    },

    saveQuantity() {
      const index = this.items.findIndex((itm) => itm.ean === this.editingItem.ean);
      if (index > -1) {
        this.items[index].cantidad = this.editingItem.cantidad;
        this.updateTotal();
      }
      this.quantityDialog = false;
      this.editingItem = null;
    },

    // Totals
    updateTotal() {
      this.totals.qty = 0;
      this.totals.total = 0;

      this.items.forEach((itm) => {
        this.totals.qty += parseFloat(itm.cantidad);
        this.totals.total += parseFloat(itm.cantidad) * parseFloat(itm.precio);
      });
    },

    // Save transfer
    save_receipt() {
      if (!this.validatemotive) return;

      if (this.items.length === 0) {
        this.showMessage("No hay productos para trasladar", "warning");
        return;
      }

      const qry = {
        doc: {
          doc_type: "TB",
          doc_value: this.totals.total,
          store: this.client.PartyIdentification,
          userId: window.profile.user_email,
          vendor: window.store.store_id,
        },
        items: this.items,
        paidTo: this.client,
        seller: window.seller,
        store: window.store.store_id,
        payments: [
          {
            payment_code: this.motive.codigo,
            payment_concept: this.motive.valor,
            payment_reference: "",
            payment_value: this.totals.total,
          },
        ],
      };

      this.loading_status = true;
      webserver(
        "put_stock",
        qry,
        (data) => {
          this.loading_status = false;
          this.print_receipt(data);
          this.resetDoc();
          this.showMessage("Traslado registrado exitosamente", "success");
        },
        (error) => {
          this.loading_status = false;
          this.showMessage("Error al registrar traslado: " + error, "error");
        }
      );
    },

    resetDoc() {
      this.motive = { codigo: null, valor: null };
      this.items = [];
      this.client = {
        PartyName: null,
        PartyIdentification: null,
        client_email: null,
        client_phone: null,
      };
      this.valide_qry = false;
      this.updateTotal();
    },

    // Print methods
    print_doc() {
      const printWindow = window.open("", "", "height=600, width=400");
      printWindow.document.write('<img src="' + this.dataURL + '" width="100%">');
      printWindow.print();
      printWindow.document.close();
      setTimeout(() => {
        printWindow.close();
      }, 500);
    },

    print_receipt(data) {
      const LS = 50;
      const itemsQty = data.items.length * LS * 3;
      const elem = document.createElement("canvas");
      elem.width = 945;
      elem.height = 1800 + itemsQty;
      const center = elem.width / 2;
      const left = 10;
      let line = 150;
      const right = elem.width - left;
      const ctx = elem.getContext("2d");

      ctx.font = "bold 40px Arial";
      ctx.textAlign = "center";

      ctx.font = "bold 60px Arial";
      ctx.fillText(window.seller.PartyIdentification_Id, center, line);
      line += LS;
      ctx.font = "bold 40px Arial";
      ctx.fillText(window.seller.PartyName, center, line);
      line += LS;
      ctx.fillText("Nit : " + window.seller.PartyIdentification, center, line);
      line += LS;
      ctx.fillText(
        window.seller.AddressLine + ", " + window.seller.CityName,
        center,
        line
      );
      line += LS;
      ctx.font = "bold 35px Arial";
      ctx.fillText("TRASLADO DE BODEGA", center, line);
      ctx.font = "bold 40px Arial";
      line += LS;
      ctx.fillText("No. " + data.document.doc_number, center, line);
      line += LS;
      ctx.fillText(data.document.issueDate, center, line);
      line += LS;
      ctx.textAlign = "start";
      ctx.fillText("Destino: " + data.buyer.PartyIdentification, left, line);
      line += LS;
      ctx.fillText("Nombre : " + data.buyer.PartyName, left, line);
      line += LS;
      line += LS;
      ctx.font = "bold 40px Arial";
      ctx.textAlign = "center";
      ctx.fillText("Productos", center, line);
      line += LS;
      ctx.beginPath();
      ctx.moveTo(left, line - 10);
      ctx.lineTo(right, line - 10);
      ctx.stroke();
      line += LS;

      data.items.forEach((row) => {
        const price = parseFloat(row.precio) * parseFloat(row.cantidad);
        const description =
          row.marca + "-" + row.refe + "-" + row.color + "-" + row.talla;
        ctx.textAlign = "start";
        ctx.fillText(description, left, line);
        ctx.textAlign = "end";
        ctx.fillText(price.toLocaleString(), right, line);
        line += LS;
      });

      ctx.beginPath();
      ctx.moveTo(left, line - 10);
      ctx.lineTo(right, line - 10);
      ctx.stroke();
      line += LS;
      line += LS;

      ctx.textAlign = "center";
      ctx.fillText("Motivo", center, line);
      line += LS;

      data.payments.forEach((row) => {
        ctx.textAlign = "start";
        ctx.fillText(row.payment_concept, left, line);
        ctx.textAlign = "end";
        ctx.fillText(parseInt(row.payment_value).toLocaleString(), right, line);
        line += LS;
      });

      this.dataURL = elem.toDataURL();
      this.showPrint = true;
    },
  },

  watch: {
    motive() {
      this.valide_qry =
        this.motive.codigo &&
        this.totals.total > 0 &&
        this.client.PartyName &&
        this.items.length > 0;
    },
    totals: {
      deep: true,
      handler() {
        this.valide_qry =
          this.motive.codigo &&
          this.totals.total > 0 &&
          this.client.PartyName &&
          this.items.length > 0;
      },
    },
    client: {
      deep: true,
      handler() {
        this.valide_qry =
          this.motive.codigo &&
          this.totals.total > 0 &&
          this.client.PartyName &&
          this.items.length > 0;
      },
    },
  },
};
</script>

<style scoped>
/* KPI Cards */
.kpi-card {
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
}

.kpi-card .v-card__text {
  display: flex;
  align-items: center;
  gap: 16px;
}

.kpi-icon {
  background-color: #f5f5f5;
  border-radius: 12px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kpi-icon .v-icon {
  font-size: 28px !important;
}

.kpi-content {
  flex: 1;
  min-width: 0;
}

.kpi-label {
  font-size: 0.875rem;
  color: #666;
  font-weight: 500;
  margin-bottom: 4px;
}

.kpi-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #333;
  line-height: 1.2;
}

.kpi-subtitle {
  font-size: 0.75rem;
  color: #999;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Info Cards */
.info-card {
  border: 1px solid #e0e0e0;
  height: 100%;
}

.card-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #333;
}

.info-item {
  margin-bottom: 4px;
}

.destination-info {
  background-color: #f5f5f5;
  padding: 12px;
  border-radius: 8px;
  margin-top: 8px;
}

.destination-info .info-item {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

/* Main Card */
.main-card {
  border: 1px solid #e0e0e0;
}

.minimal-toolbar {
  border-bottom: 1px solid #e0e0e0;
  padding: 16px 24px;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
}

/* Data Table */
.minimal-table {
  border: none !important;
}

.minimal-table >>> .v-data-table__wrapper {
  border: none;
}

.minimal-table >>> thead tr th {
  background-color: #fafafa !important;
  font-weight: 600 !important;
  color: #666 !important;
  border-bottom: 2px solid #e0e0e0 !important;
  font-size: 0.875rem !important;
  padding: 16px !important;
}

.minimal-table >>> tbody tr {
  border-bottom: 1px solid #f0f0f0 !important;
  transition: background-color 0.2s;
}

.minimal-table >>> tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.minimal-table >>> tbody tr td {
  padding: 16px !important;
  font-size: 0.875rem !important;
}

/* Product info */
.product-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.product-main {
  font-weight: 500;
  color: #333;
}

.product-details {
  color: #666;
}

/* Quantity edit */
.quantity-edit {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
}

.quantity-edit:hover .v-chip {
  transform: scale(1.05);
}

/* Dialog */
.dialog-toolbar {
  border-bottom: 1px solid #e0e0e0;
}

.dialog-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
}

.search-filters {
  background-color: #fafafa;
}

.search-table >>> .v-data-table__wrapper {
  border: none;
}

/* Loading */
.minimal-table >>> .v-data-table__progress {
  height: 2px !important;
}

/* Responsive */
@media (max-width: 600px) {
  .kpi-value {
    font-size: 1.25rem;
  }

  .section-title {
    font-size: 1.125rem;
  }

  .kpi-card .v-card__text {
    flex-direction: column;
    text-align: center;
    padding: 16px !important;
  }

  .kpi-icon {
    margin-bottom: 8px;
  }

  .minimal-toolbar {
    padding: 12px 16px !important;
  }

  .dialog-title {
    font-size: 1.125rem;
  }
}

@media (max-width: 960px) {
  .kpi-value {
    font-size: 1.5rem;
  }

  .section-title {
    font-size: 1.25rem;
  }

  .kpi-card .v-card__text {
    flex-direction: row;
    text-align: left;
  }
}

/* Text utilities */
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
